import { useState } from 'react'

export type Lid = ReturnType<typeof useLid>

export type LidOperation = 'open' | 'close' | 'reset' | 'toggle'

/**
 * Represents options for changing the lid state.
 *
 * @property callback - An optional callback function to be executed.
 * @property delay - An optional delay time in milliseconds.
 * @property debounceTimeout - An optional debounce timeout in milliseconds.
 */
export type LidOperationOptions = {
  callback?: () => void
  delay?: number
  debounceTimeout?: number
}

/** Custom hook to manage the state of a lid (open/close). */
export function useLid(options?: { initialState?: boolean }) {
  const [isOpen, setIsOpen] = useState(options?.initialState ?? false)
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null,
  )

  const isClosed = !isOpen
  const isReset = isOpen === (options?.initialState ?? false)

  /** Applies the provided operation to the lid state either immediately or after an optional debounce period. */
  function _apply(operation: LidOperation, _options?: LidOperationOptions) {
    /** Sets the lid state and invokes an optional callback */
    function doOperation() {
      switch (operation) {
        case 'open':
          setIsOpen(true)
          break
        case 'close':
          setIsOpen(false)
          break
        case 'reset':
          setIsOpen(options?.initialState ?? false)
          break
        case 'toggle':
          setIsOpen((prev) => !prev)
          break
      }

      if (_options?.callback) {
        _options.callback()
      }
    }

    /** Invokes `doOperation` immediately or with an optional delay */
    function dispatchOperation() {
      if (_options?.delay) {
        setTimeout(doOperation, _options.delay)
      } else {
        doOperation()
      }
    }

    if (!_options?.debounceTimeout) {
      dispatchOperation()
      return
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    setDebounceTimer(setTimeout(dispatchOperation, _options.debounceTimeout))
  }

  /** Closes the lid */
  function close(_options?: LidOperationOptions) {
    _apply('close', _options)
  }

  /** Opens the lid */
  function open(_options?: LidOperationOptions) {
    _apply('open', _options)
  }

  /** Resets the lid to its default state */
  function reset(_options?: LidOperationOptions) {
    _apply('reset', _options)
  }

  /** Toggles the lid state to the opposite state */
  function toggle(_options?: LidOperationOptions) {
    _apply('toggle', _options)
  }

  /** Sets the lid to a specified state. Handy when the lid state must be derived. */
  function set(state: boolean, _options?: LidOperationOptions) {
    _apply(state ? 'open' : 'close', _options)
  }

  return {
    close,
    isClosed,
    isOpen,
    isReset,
    open,
    reset,
    set,
    toggle,
  }
}
